// components/chat/CallOutgoingCard.jsx
// Caller-side status card — spec 03 (outgoing/ringing) with its variants
// (offline / no-answer / declined) and spec 05 (callee busy). Driven by the
// provider's `outgoing` object; all actions are provider callbacks.
//   outgoing = { status, targetUser, audioOnly?, replyText? }
//   status ∈ ringing | offline | no_answer | declined | busy
import React from "react";
import { Video, Mic, PhoneOff, Phone, MessageCircle, Clock, Bell } from "lucide-react";

const GREEN = "#00FF99";
const RED   = "#ff5f56";
const AMBER = "#FFC24B";

const ini = (n) => {
  const p = (n || "?").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "?") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
};

const CSS = `
@keyframes kwOutRing{0%{transform:scale(.9);opacity:0}25%{opacity:1}100%{transform:scale(1.08);opacity:0}}
@keyframes kwDots{0%{content:""}25%{content:"."}50%{content:".."}75%{content:"..."}}
.kw-dots::after{content:"";animation:kwDots 1.4s steps(4) infinite}
`;

function Avatar({ name, accent, rings }) {
  const ringBase = { position: "absolute", borderRadius: "999px", border: `1.5px solid ${accent}66`, animation: "kwOutRing 1.8s ease-out infinite", pointerEvents: "none" };
  return (
    <div style={{ position: "relative", width: 52, height: 52, flex: "none" }}>
      <div style={{
        width: 52, height: 52, borderRadius: "999px", background: "radial-gradient(circle at 35% 30%,#1c2b24,#0b120e 70%)",
        border: `1px solid ${accent}73`, display: "grid", placeItems: "center",
        fontFamily: "'Space Grotesk',sans-serif", fontSize: 17, fontWeight: 600, color: accent,
      }}>{ini(name)}</div>
      {rings && <>
        <span style={{ ...ringBase, inset: -7 }} />
        <span style={{ ...ringBase, inset: -14, borderColor: `${accent}2e`, animationDelay: ".45s" }} />
      </>}
    </div>
  );
}

const ghostBtn = {
  flex: 1, display: "flex", gap: 7, alignItems: "center", justifyContent: "center",
  borderRadius: 999, padding: 10, fontSize: 12, fontWeight: 600, cursor: "pointer",
  background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.55)",
};
const greenBtn = { ...ghostBtn, background: GREEN, border: "none", color: "#000" };
const redFull  = { ...ghostBtn, background: "rgba(255,95,86,0.14)", border: `1px solid ${RED}66`, color: RED };

export default function CallOutgoingCard({ outgoing, onCancel, onDismiss, onRetry, onLeaveMessage, onPingWhenFree }) {
  if (!outgoing) return null;
  const { status, targetUser, audioOnly, replyText } = outgoing;
  const name = targetUser?.full_name || targetUser?.name || "Someone";
  const TypeIcon = audioOnly ? Mic : Video;

  const accent = status === "busy" ? AMBER : (status === "no_answer" || status === "declined") ? RED : GREEN;
  const border = status === "busy" ? `${AMBER}59` : (status === "no_answer" || status === "declined") ? "rgba(255,95,86,0.35)" : `${GREEN}4d`;

  let line, StatusIcon, statusColor, actions;
  if (status === "ringing") {
    StatusIcon = TypeIcon; statusColor = GREEN;
    line = <span className="kw-dots">Calling</span>;
    actions = <button onClick={onCancel} style={redFull}><PhoneOff size={15} strokeWidth={1.9} /> Cancel</button>;
  } else if (status === "offline") {
    StatusIcon = TypeIcon; statusColor = "rgba(255,255,255,0.55)";
    line = "Offline — leave a message?";
    actions = <>
      <button onClick={onLeaveMessage} style={ghostBtn}><MessageCircle size={15} strokeWidth={1.9} /> Leave a message</button>
      <button onClick={onDismiss} style={ghostBtn}>Dismiss</button>
    </>;
  } else if (status === "no_answer") {
    StatusIcon = PhoneOff; statusColor = RED;
    line = "No answer";
    actions = <>
      <button onClick={onLeaveMessage} style={ghostBtn}><MessageCircle size={15} strokeWidth={1.9} /> Message</button>
      <button onClick={onRetry} style={greenBtn}><Phone size={15} strokeWidth={1.9} color="#000" /> Try again</button>
    </>;
  } else if (status === "declined") {
    StatusIcon = PhoneOff; statusColor = RED;
    line = replyText ? `“${replyText}”` : "Declined";
    actions = <button onClick={onDismiss} style={ghostBtn}>Dismiss</button>;
  } else { // busy (spec 05)
    StatusIcon = Clock; statusColor = AMBER;
    line = "On another call · notified you tried";
    actions = <>
      <button onClick={onLeaveMessage} style={ghostBtn}><MessageCircle size={15} strokeWidth={1.9} /> Leave a message</button>
      <button onClick={onPingWhenFree} style={ghostBtn}><Bell size={15} strokeWidth={1.9} /> Ping me when free</button>
    </>;
  }

  return (
    <>
      <style>{CSS}</style>
      <div style={{
        position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)", zIndex: 10082,
        width: 320, padding: 14, background: "rgba(13,13,13,0.97)", border: `1px solid ${border}`,
        borderRadius: 20, boxShadow: "0 16px 50px rgba(0,0,0,.6),0 0 24px rgba(0,255,153,.06)", backdropFilter: "blur(12px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={name} accent={accent} rings={status === "ringing"} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{name}</div>
            <div style={{ fontSize: 10.5, marginTop: 2, display: "flex", gap: 6, alignItems: "center", color: statusColor }}>
              <StatusIcon size={11} strokeWidth={1.9} /> {line}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 13 }}>{actions}</div>
      </div>
    </>
  );
}
